import { buildPublicAppUrl } from "@/lib/appConfig";
import { SHOPIFY_APP_STORE_URL } from "@/lib/appLinks";

const SITE_NAME = "Splitkart";
const SUPPORT_EMAIL = "support@shakatah.com";
const LOGO_URL = buildPublicAppUrl("/favicon.png?v=7");

export const pricingPlans = [
  {
    name: "Free",
    price: "0",
    freeSplits: 5,
    commission: "2%",
    description: "Free Splitkart plan with 5 free splits every billing cycle after the intro trial.",
  },
  {
    name: "Starter",
    price: "11.99",
    freeSplits: 25,
    commission: "1.5%",
    description: "Starter Splitkart plan with 25 free splits every billing cycle and lower overage commission.",
  },
  {
    name: "Growth",
    price: "29.99",
    freeSplits: 100,
    commission: "1%",
    description: "Growth Splitkart plan for higher split volume with the lowest overage commission.",
  },
];

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": buildPublicAppUrl("/#organization"),
    name: SITE_NAME,
    url: buildPublicAppUrl("/"),
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
    email: SUPPORT_EMAIL,
    contactPoint: [
      {
        "@type": "ContactPoint",
        email: SUPPORT_EMAIL,
        contactType: "customer support",
        availableLanguage: ["en"],
      },
    ],
    sameAs: [SHOPIFY_APP_STORE_URL],
  };
}

export function websiteSchema() {
  return {
    "@type": "WebSite",
    "@id": buildPublicAppUrl("/#website"),
    name: SITE_NAME,
    url: buildPublicAppUrl("/"),
    publisher: {
      "@id": buildPublicAppUrl("/#organization"),
    },
  };
}

export function softwareApplicationSchema() {
  return {
    "@type": "SoftwareApplication",
    "@id": buildPublicAppUrl("/#software-application"),
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Group gifting and split payments",
    operatingSystem: "Web, Shopify, WooCommerce",
    url: buildPublicAppUrl("/"),
    installUrl: SHOPIFY_APP_STORE_URL,
    description:
      "Splitkart helps Shopify and WooCommerce merchants offer group gifting and split payments for high-ticket orders.",
    publisher: {
      "@id": buildPublicAppUrl("/#organization"),
    },
    offers: pricingPlans.map((plan) => ({
      "@type": "Offer",
      name: `${plan.name} Plan`,
      price: plan.price,
      priceCurrency: "USD",
      category: "subscription",
      availability: "https://schema.org/InStock",
      url: buildPublicAppUrl("/pricing"),
      description: plan.description,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: plan.price,
        priceCurrency: "USD",
        billingIncrement: 1,
        unitText: "month",
      },
    })),
  };
}

export function baseSiteSchemaGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [organizationSchema(), websiteSchema(), softwareApplicationSchema()],
  };
}

export function pricingPageSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema(),
      softwareApplicationSchema(),
      {
        "@type": "WebPage",
        "@id": buildPublicAppUrl("/pricing#webpage"),
        name: "Splitkart Pricing",
        url: buildPublicAppUrl("/pricing"),
        description:
          "Splitkart pricing plans for Shopify and WooCommerce merchants, including free splits, paid plans, and overage commission.",
        isPartOf: {
          "@id": buildPublicAppUrl("/#website"),
        },
        about: {
          "@id": buildPublicAppUrl("/#software-application"),
        },
      },
    ],
  };
}

export function howToSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": buildPublicAppUrl("/how-split-payments-work#howto"),
    name: "How Splitkart split payments work",
    description:
      "A customer starts a split purchase, invites friends, each participant pays their share, and the merchant receives one final order after full payment.",
    totalTime: "PT5M",
    supply: [
      {
        "@type": "HowToSupply",
        name: "Shopify or WooCommerce store",
      },
      {
        "@type": "HowToSupply",
        name: "Splitkart split payment link",
      },
    ],
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Organizer starts the split",
        text: "The shopper chooses Split with Friends and enters delivery and participant details.",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Organizer pays their share",
        text: "The organizer pays their own portion first to activate the split payment.",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Friends receive contribution links",
        text: "Each invited participant receives a secure link to pay only their share.",
      },
      {
        "@type": "HowToStep",
        position: 4,
        name: "Merchant receives one final order",
        text: "After all shares are paid, Splitkart creates the final shippable order for the merchant.",
      },
    ],
  };
}

export function videoObjectSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": buildPublicAppUrl("/#homepage-video"),
    name: "Splitkart split payment checkout demo",
    description: "A short product demo showing how Splitkart helps shoppers split checkout costs with friends.",
    thumbnailUrl: [buildPublicAppUrl("/og-image.png")],
    uploadDate: "2026-07-01",
    contentUrl: buildPublicAppUrl("/splitkart-screen-recording.mp4"),
    embedUrl: buildPublicAppUrl("/"),
    publisher: {
      "@id": buildPublicAppUrl("/#organization"),
    },
  };
}
